import { ActiveIcon, BackArrowIcon, CopyIcon, InActiveIcon, SuspendIcon } from "@/assets/icons/admin";
import { Button, Textarea } from "@nextui-org/react";
import RequestRejectDialog from "./requestRejectDialog";
import SuccessResponseDialog from "./successResponseDialog";
import { RequestDetails } from "@/types";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useGetAllRequests, useRespondRequest } from "@/services/request.service";

interface RequestDetailTabProps {
    onCancel?: () => void;
    item: RequestDetails | undefined | null
}

const RequestDetailTab: React.FC<RequestDetailTabProps> = ({ onCancel, item }) => {

    const [requestId, setRequestId] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [sentDate, setSentDate] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [topic, setTopic] = useState<string>('');
    const [title, setTitle] = useState<string>('');
    const [question, setQuestion] = useState<string>('');
    const [message, setMessage] = useState<string>('');
    const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);

    useEffect(() => {
        if (item) {
            setRequestId(item.id)
            setStatus(item.status)
            setSentDate(item.createdAt)
            setName(item.userName)
            setEmail(item.userEmail)
            setTopic(item.topic)
            setTitle(item.title)
            setQuestion(item.question)
        }
    }, [item]);

    const { refetch } = useGetAllRequests({ page: 1, pageSize: 10 });

    const formatDateTime = (isoString: string | number | Date) => {
        const date = new Date(isoString);

        const padZero = (num: number) => (num < 10 ? "0" : "") + num;

        const day = padZero(date.getDate());
        const month = padZero(date.getMonth() + 1); // getMonth() returns month from 0 to 11
        const year = date.getFullYear(); // Get full year
        const hours = padZero(date.getHours());
        const minutes = padZero(date.getMinutes());
        const seconds = padZero(date.getSeconds()); // Add this line to get seconds

        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`; // Include seconds in the returned string
    };

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            console.log(`Copied: ${text}`);
            toast.success('Copied text')
        }).catch((err) => {
            console.error('Failed to copy: ', err);
            toast.error('Error when copying text')
        });
    };

    const { mutate: respondRequest } = useRespondRequest()
    function handleSubmit() {
        respondRequest({ requestId: requestId, response: message }, {
            onSuccess: () => {
                setStatus('SOLVED'); // Set status to 'SOLVED'
                console.log('handle submit')
                refetch()
                setShowSuccessDialog(true);
            },
            onError: (error) => {
                console.error(error);
                refetch()
                toast.error('Error when responding to the request')
            }
        })
    }

    function handleReject() {
        setStatus('REJECTED')
    }

    return (
        <div>
            {/* back button */}
            <div className="flex mb-4">
                <div className="font-ubuntu w-full text-center text-2xl font-semibold text-white sm:float-left">
                    <span>
                        {onCancel && (
                            <Button
                                isIconOnly
                                onClick={onCancel}
                                className="transparent-bg float-left ml-[-11px] h-auto w-auto"
                            >
                                <BackArrowIcon />
                            </Button>
                        )}
                        <p className="text-sm sm:float-left sm:text-lg md:text-lg lg:text-lg">
                            Request #{requestId}
                        </p>
                    </span>
                </div>
            </div>
            {/* Details component */}
            <div className="flex-col w-full p-2 request-detail-block">
                <div className="w-full flex flex-col sm:flex-row items-center justify-evenly p-0">
                    <div className="w-full sm:w-1/2 p-2 sm:p-4">
                        <label
                            htmlFor="firstName"
                            className="field-label mb-2 block text-sm font-normal text-white"
                        >
                            Status
                        </label>
                        <div className="flex items-center">
                            {status === "WAITING" ? (
                                <>
                                    <SuspendIcon />
                                    <span className="text-[#FFEE00] font-openSans font-bold text-sm ml-2">
                                        Waiting for an Answer
                                    </span></>
                            ) : status === "SOLVED" ? (
                                <>
                                    <ActiveIcon />
                                    <span className="text-[#54D62C] font-openSans font-bold text-sm ml-2">
                                        Solved
                                    </span>
                                </>
                            ) : (
                                <>
                                    <InActiveIcon />
                                    <span className="text-[#FF4842] font-openSans font-bold text-sm ml-2">
                                        Rejected
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="w-full sm:w-1/2 mt-4 sm:mt-0 p-2 sm:p-4">
                        <label
                            htmlFor="firstName"
                            className="field-label mb-2 block text-sm font-normal text-white"
                        >
                            Sent Date
                        </label>
                        <div className="flex items-center">
                            <span className="text-white font-openSans font-bold text-sm">
                                {formatDateTime(sentDate)}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="w-full flex flex-col sm:flex-row items-center justify-evenly p-0">
                    <div className="w-full sm:w-1/2 p-2 sm:px-4 sm:py-3">
                        <label
                            htmlFor="firstName"
                            className="field-label mb-2 block text-sm font-normal text-white"
                        >
                            Name
                        </label>
                        <div className="flex items-center justify-between">
                            <span className="text-[#2998FF] font-openSans font-bold text-sm">
                                {name}
                            </span>
                            <span className="">
                                <Button
                                    isIconOnly
                                    onClick={() => handleCopy(name)}
                                    className="transparent-bg h-6 w-6"
                                >
                                    <CopyIcon />
                                </Button>
                            </span>
                        </div>
                    </div>
                    <div className="w-full sm:w-1/2 mt-4 sm:mt-0 p-2 sm:px-4 sm:py-3">
                        <label
                            htmlFor="firstName"
                            className="field-label mb-2 block text-sm font-normal text-white"
                        >
                            Email
                        </label>
                        <div className="flex items-center justify-between">
                            <span className="text-white font-openSans font-bold text-sm">
                                {email}
                            </span>
                            <span className="">
                                <Button
                                    isIconOnly
                                    onClick={() => handleCopy(email)}
                                    className="transparent-bg h-6 w-6"
                                >
                                    <CopyIcon />
                                </Button>
                            </span>
                        </div>
                    </div>
                </div>
                <div className="w-full flex flex-col sm:flex-row items-center justify-evenly p-0">
                    <div className="w-full sm:w-1/2 p-2 sm:p-4">
                        <label
                            htmlFor="firstName"
                            className="field-label mb-2 block text-sm font-normal text-white"
                        >
                            Topic
                        </label>
                        <div className="flex items-center">
                            <span className="text-white font-openSans font-bold text-sm">
                                {topic}
                            </span>
                        </div>
                    </div>
                    <div className="w-full sm:w-1/2 mt-4 sm:mt-0 p-2 sm:p-4">
                        <label
                            htmlFor="firstName"
                            className="field-label mb-2 block text-sm font-normal text-white"
                        >
                            Title
                        </label>
                        <div className="flex items-center">
                            <span className="text-white font-openSans font-bold text-sm">
                                {title}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="w-full flex flex-col sm:flex-row items-center p-0">
                    <div className="w-full sm:w-1/2 p-2 sm:p-4">
                        <label
                            htmlFor="firstName"
                            className="field-label mb-2 block text-sm font-normal text-white"
                        >
                            Question
                        </label>
                        <div className="flex items-center">
                            <span className="text-white font-openSans font-bold text-sm">
                                {question}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            {/* Response component */}
            <div className="flex-col h-full mt-4">
                <div className="hidden sm:flex my-4">
                    <span className="font-kanit text-lg font-medium text-white">Response for User</span>
                </div>
                <div className="flex flex-col basis-full my-4">
                    <label
                        htmlFor="firstName"
                        className="field-label mb-2 block text-sm font-normal text-white"
                    >
                        Message
                    </label>
                    <Textarea
                        className={`input-bg !cursor-text rounded-md border border-solid`}
                        classNames={{
                            inputWrapper: [
                                "!cursor-text",
                                "bg-white-5",
                                "response-field-height", "text-sm"
                            ],
                        }}
                        variant="bordered"
                        placeholder="Type Here"
                        onChange={(e) => setMessage(e.target.value)} // This should work now
                    />
                </div>
            </div>
            {/* Buttons */}
            <div className="hidden sm:flex mt-4 basis-1/2 items-center justify-between">
                <div className="flex"> </div>
                <div className="flex">
                    <RequestRejectDialog
                        requestId={requestId}
                        onReject={handleReject}
                        trigger={
                            <Button className="bg-[#FF5E5E] mr-2 font-ubuntu w-full rounded-md pl-16 pr-16 text-base font-bold">
                                Reject Question
                            </Button>
                        } />
                    <Button onClick={handleSubmit} className="admin-common-button-gradient font-ubuntu w-full rounded-md pl-16 pr-16 text-base font-bold">
                        Send Email
                    </Button>
                    {showSuccessDialog && (
                        <SuccessResponseDialog isOpen={showSuccessDialog} onClose={() => setShowSuccessDialog(false)} />
                    )}
                </div>
            </div>
            <div className="flex flex-col sm:hidden mt-4 basis-full items-center space-y-4">
                {showSuccessDialog && (
                    <SuccessResponseDialog isOpen={showSuccessDialog} onClose={() => setShowSuccessDialog(false)} />
                )}
                <Button onClick={handleSubmit} className="admin-common-button-gradient font-ubuntu w-full rounded-md pl-16 pr-16 text-base font-bold">
                    Send Email
                </Button>
                <RequestRejectDialog
                    requestId={requestId}
                    onReject={handleReject}
                    trigger={
                        <Button className="bg-[#FF5E5E] font-ubuntu w-full rounded-md pl-16 pr-16 text-base font-bold">
                            Reject Question
                        </Button>
                    } />
            </div>
        </div>
    )
}

export default RequestDetailTab;